import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Button } from 'components/Button';
import { MobileAction } from 'components/Layout';
import { theme, toRem } from 'design';
import { useWindowSize } from 'hooks';
import routeConstants, { adminNav, employeeNav } from 'routes/routeConstants';
import { PermissionGate } from 'screens/Security';
import { selectCurrentEmployee } from 'store/App';
import { EmployeeRole } from 'types';
import { Logo } from '../Logo';
import NavItem from './NavItem';
import { NavItemBaseProps } from './NavItemProps';

interface INavProps {
  isOpen?: boolean;
  className?: string;
  dataCy?: string;
  isHidden?: boolean;
  onNavOpenChange(isOpen: boolean): void;
}

const PADDING_TOP_DESKTOP = theme.header.height + theme.space['3xl'];

const Layout = styled.nav.attrs({
  role: 'navigation',
})<Partial<INavProps>>`
  position: fixed;
  left: -100%;
  z-index: ${theme.zIndex.header + 10};
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  padding: 0 ${toRem(theme.space.xl)};
  background-color: ${theme.color.neutral[600]};
  overflow: scroll;
  transition: left ${theme.transition.nav};

  ${({ isHidden }) =>
    isHidden &&
    css`
      display: none;
    `}

  ${({ isOpen }) =>
    isOpen &&
    css`
      left: 0;
      overflow: hidden;
    `}

  @media (min-width: ${theme.breakpoint.small}px) {
    position: fixed;
    top: 0;
    left: 0;
    z-index: ${theme.zIndex.nav};
    flex-shrink: 0;
    align-items: flex-start;
    width: ${toRem(theme.nav.width.closed)};
    padding: ${toRem(PADDING_TOP_DESKTOP)} ${toRem(theme.space.md)} ${toRem(theme.space['2xl'])};
    border-right: ${toRem(1)} solid ${theme.color.neutral[200]};
    background-color: ${theme.color.white};
    overflow: hidden;
    transition: width ${theme.transition.nav};

    ${({ isOpen }) =>
      isOpen &&
      css`
        width: ${toRem(theme.nav.width.open)};
      `}

    > * {
      width: auto;
      max-width: auto;
    }
  }
`;

const Children = styled.section`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin-top: ${toRem(theme.space['2xl'])};

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-top: 0;
  }
`;

const StyledLogo = styled(Logo)<Partial<INavProps>>`
  display: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: flex;
    width: ${toRem(27)};
    transform: translateX(${toRem(10)});
    transition: transform ${theme.transition.nav};

    ${({ isOpen }) =>
      isOpen &&
      css`
        transform: translateX(${toRem(27)});
      `}
  }
`;

const Nav: React.FC<INavProps> = ({ isOpen, isHidden, className, dataCy, onNavOpenChange }) => {
  const location = useLocation();
  const { width } = useWindowSize();
  const history = useHistory();
  const currentPage = location.pathname;
  const currentEmployee = useSelector(selectCurrentEmployee());
  const navigation = currentEmployee ? adminNav : employeeNav;

  const handleMobileNavClick = () => {
    if (width <= theme.breakpoint.small) {
      onNavOpenChange(false);
    }
  };

  return (
    <Layout isOpen={isOpen} isHidden={isHidden} className={className} data-cy={dataCy}>
      <Children>
        {navigation.map((item: NavItemBaseProps) => {
          if (item.label === 'Dashboard') {
            return (
              <NavItem
                key={item.label}
                label={item.label}
                icon={item.icon}
                iconActive={item.iconActive}
                itemActive={currentPage === routeConstants.private.rootPath}
                navOpen={isOpen}
                onClick={handleMobileNavClick}
                to={item?.to || ''}
                exact
              />
            );
          }
          return (
            <NavItem key={item.label} label={item.label} icon={item.icon} iconActive={item.iconActive} itemActive={item.to?.split('/')[1] === currentPage.split('/')[1]} navOpen={isOpen} onClick={handleMobileNavClick} to={item?.to || ''} />
          );
        })}
        <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN, EmployeeRole.MANAGER]}>
          <MobileAction>
            <Button
              data-cy="make-payment-button-m"
              label="Make Payment"
              appearance="tertiary"
              size="large"
              onClick={() => {
                handleMobileNavClick();
                history.push(routeConstants.private.makePayment);
              }}
            />
          </MobileAction>
        </PermissionGate>
      </Children>
      <StyledLogo isOpen={isOpen} />
    </Layout>
  );
};

export default Nav;
